import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Play, ImageOff, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";
import UserGalleryService from "../services/user/UserGalleryService";



export default function GalleryPage() {
  const gallerySectionRef = useRef(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryItems, setGalleryItems] = useState([]);
  const [error, setError] = useState(null);
  const itemsPerPage = 20;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch gallery data from API
  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await UserGalleryService.getGalleryItems();
        // Assuming response structure is { data: [...] } or just the array
        const items = Array.isArray(response.data) ? response.data :
          Array.isArray(response) ? response : [];
        setGalleryItems(items);
      } catch (err) {
        console.error("Error fetching gallery:", err);
        setError("Failed to load gallery items. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(galleryItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = galleryItems.slice(startIndex, endIndex);

  const openModal = (item, index) => {
    setSelectedMedia(item);
    setCurrentIndex(startIndex + index);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedMedia(null);
    document.body.style.overflow = "unset";
  };

  const navigateModal = (direction) => {
    const newIndex =
      direction === "next"
        ? (currentIndex + 1) % galleryItems.length
        : (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    setCurrentIndex(newIndex);
    setSelectedMedia(galleryItems[newIndex]);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    gallerySectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") navigateModal("prev");
      if (e.key === "ArrowRight") navigateModal("next");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMedia, currentIndex]);

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="relative h-[300px] md:h-[400px] overflow-hidden bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9]">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=2000&h=800&fit=crop")' }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="relative h-full max-w-[1400px] mx-auto px-6 flex items-center justify-center text-center">
          <div className="w-full">
            <h1
              className="text-white font-medium font-serif leading-tight mb-4"
              style={{
                fontSize: "clamp(36px, 6vw, 64px)",
              }}
            >
              Rental Gallery
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
              Explore our premium collection of rental equipment, elegant event setups, and stunning venue transformations.
            </p>
          </div>
        </div>
      </section>

      {/* Polaroid Gallery Grid */}
      <section
        ref={gallerySectionRef}
        className="py-20 md:py-28 px-6 bg-white dark:bg-[#121212] overflow-hidden scroll-mt-24"
      >
        <div className="max-w-[1400px] mx-auto">
          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {Array.from({ length: 20 }).map((_, index) => (
                <div
                  key={index}
                  className="group relative"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`,
                  }}
                >
                  <div className="bg-white dark:bg-[#F5F5F5] p-3 pb-12 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
                    <div className="relative aspect-square overflow-hidden bg-[#E9E9E9] dark:bg-[#D0D0D0]">
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent shimmer"></div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 text-center">
                      <div className="h-4 bg-[#E9E9E9] dark:bg-[#D0D0D0] rounded mx-auto w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div
              className="flex flex-col items-center justify-center py-20 md:py-32"
              style={{
                animation: "fadeInUp 0.6s ease-out both",
              }}
            >
              <div className="w-24 h-24 md:w-32 md:h-32 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mb-6">
                <TriangleAlert
                  size={48}
                  className="text-red-500 dark:text-red-400"
                />
              </div>
              <h2
                className="text-red-600 dark:text-red-400 font-medium font-serif mb-3 text-center"
                style={{
                  fontSize: "clamp(24px, 4vw, 36px)",
                }}
              >
                Failed to Load Gallery
              </h2>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-base md:text-lg text-center max-w-md mb-8 px-4">
                {error}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white text-sm md:text-base font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && galleryItems.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-20 md:py-32"
              style={{
                animation: "fadeInUp 0.6s ease-out both",
              }}
            >
              <div className="w-24 h-24 md:w-32 md:h-32 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6">
                <ImageOff
                  size={48}
                  className="text-[#62397f] dark:text-[#A78BFA]"
                />
              </div>
              <h2
                className="text-[#333333] dark:text-white font-medium font-serif mb-3 text-center"
                style={{
                  fontSize: "clamp(24px, 4vw, 36px)",
                }}
              >
                No Gallery Items Yet
              </h2>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-base md:text-lg text-center max-w-md mb-8 px-4">
                Check back soon for stunning event photos and videos from our
                premium rentals
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/rentals"
                  className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white text-sm md:text-base font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02] shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
                >
                  Browse Rentals
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 md:px-8 py-3 md:py-4 bg-[#F5F3FF] dark:bg-[#2A2440] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] text-[#62397f] dark:text-[#A78BFA] text-sm md:text-base font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          )}

          {/* Polaroid Grid */}
          {!loading && galleryItems.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {currentItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="group relative"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={() => openModal(item, index)}
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.05}s both`,
                    }}
                  >
                    {/* Polaroid Frame */}
                    <div
                      className="bg-white dark:bg-[#F5F5F5] p-3 pb-12 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] transition-all duration-300 cursor-pointer"
                      style={{
                        transform:
                          hoveredIndex === index
                            ? "rotate(0deg) scale(1.05)"
                            : `rotate(${((index % 3) - 1) * 3}deg)`,
                      }}
                    >
                      {/* Image/Video Thumbnail */}
                      <div className="relative aspect-square overflow-hidden bg-[#E9E9E9] dark:bg-[#D0D0D0]">
                        {item.type === "video" ? (
                          <div className="w-full h-full relative">
                            <video
                              src={item.url}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              muted
                              playsInline
                              onMouseOver={(e) => e.target.play()}
                              onMouseOut={(e) => {
                                e.target.pause();
                                e.target.currentTime = 0;
                              }}
                            />
                          </div>
                        ) : (
                          <img
                            src={item.url}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        )}

                        {/* Video Play Button Overlay */}
                        {item.type === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-300">
                            <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-200">
                              <Play
                                size={20}
                                className="text-[#62397f] ml-1"
                                fill="#62397f"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Caption (Handwritten Style) */}
                      <div className="absolute bottom-3 left-3 right-3 text-center">
                        <p
                          className="text-sm text-[#333333] font-medium"
                          style={{
                            fontFamily: "'Indie Flower', cursive",
                          }}
                        >
                          {item.title}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-16 flex items-center justify-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${currentPage === 1
                      ? "bg-[#E9E9E9] dark:bg-[#333333] text-[#999999] dark:text-[#666666] cursor-not-allowed"
                      : "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#62397f] hover:text-white dark:hover:bg-[#62397f] transform hover:scale-110"
                      }`}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      // Show first page, last page, current page, and pages around current
                      const shouldShow =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      if (!shouldShow) {
                        // Show ellipsis
                        if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="px-2 text-[#666666] dark:text-[#A0A0A0]"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`min-w-[40px] h-10 px-3 rounded-full font-semibold text-sm transition-all duration-200 ${currentPage === page
                            ? "bg-[#62397f] text-white shadow-[0_4px_12px_rgba(139,92,246,0.3)]"
                            : "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#EDE9FE] dark:hover:bg-[#342A50] transform hover:scale-105"
                            }`}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}

                  {/* Next Button */}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${currentPage === totalPages
                      ? "bg-[#E9E9E9] dark:bg-[#333333] text-[#999999] dark:text-[#666666] cursor-not-allowed"
                      : "bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] hover:bg-[#62397f] hover:text-white dark:hover:bg-[#62397f] transform hover:scale-110"
                      }`}
                    aria-label="Next page"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}

              {/* Gallery Stats */}
              <div className="mt-8 text-center">
                <p className="text-[#666666] dark:text-[#A0A0A0] text-sm">
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, galleryItems.length)} of{" "}
                  {galleryItems.length} items
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          {/* Navigation Arrows */}
          {galleryItems.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModal("prev");
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
                aria-label="Previous"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateModal("next");
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 hover:scale-110 z-50"
                aria-label="Next"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Modal Content */}
          <div
            className="relative max-w-7xl w-full mx-6 max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Media Display */}
            <div className="bg-black rounded-2xl overflow-hidden">
              {selectedMedia.type === "video" ? (
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <video
                    src={selectedMedia.url}
                    className="absolute inset-0 w-full h-full rounded-xl"
                    controls
                    autoPlay
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <img
                  src={selectedMedia.url}
                  alt={selectedMedia.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
            </div>

            {/* Media Info */}
            <div className="mt-6 text-center">
              <h3
                className="text-white font-medium font-serif text-xl md:text-2xl mb-2"
              >
                {selectedMedia.title}
              </h3>
              <p className="text-white/80 text-sm md:text-base mb-2">
                {selectedMedia.description}
              </p>
            </div>

            {/* Counter */}
            {galleryItems.length > 1 && (
              <div className="mt-4 text-center">
                <p className="text-white/60 text-sm">
                  {currentIndex + 1} / {galleryItems.length}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
      <FloatingActions />

      {/* Animation Keyframes */}
      <style jsx="true" global="true">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </>
  );
}
